import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { HKBadge } from "@/components/site/HKBadge";
import { CTASection } from "@/components/site/CTASection";
import { INDUSTRIES } from "@/lib/content";
import { Seo } from "@/components/site/Seo";

export default function IndustriesPage() {
  const { t } = useTranslation();
  const engagesItems = t("common.industryEngages.items", { returnObjects: true }) || [];
  return (
    <div data-testid="industries-page">
      <Seo
        title={t("seo.industries.metaTitle")}
        description={t("seo.industries.metaDescription")}
        keywords={t("seo.industries.keywords")}
        path="/industries"
        breadcrumbs={[
          { name: t("seo.breadcrumbHome"), url: "/" },
          { name: t("seo.industries.breadcrumbLabel"), url: "/industries" },
        ]}
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dc-dot-grid opacity-50" aria-hidden />
        <div className="relative mx-auto w-full max-w-6xl px-4 pb-10 pt-14 sm:px-6 sm:pb-14 sm:pt-20 lg:px-8">
          <HKBadge />
          <h1 className="mt-5 max-w-4xl text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-[58px] lg:leading-[1.05]">
            {t("pages.industriesTitle")}
          </h1>
          <p className="mt-4 max-w-2xl text-base text-slate-600 sm:text-lg">{t("pages.industriesSubtitle")}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/contact" data-testid="industries-hero-cta">
              <Button className="h-11 rounded-lg bg-blue-600 px-5 text-white hover:bg-blue-700">
                {t("cta.discussVertical")} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-6xl px-4 pb-10 sm:px-6 lg:px-8">
        <div className="grid gap-5 md:grid-cols-2">
          {INDUSTRIES.map((ind, idx) => (
            <motion.a
              key={ind.key} href={`#${ind.key}`}
              initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.35, delay: idx * 0.04 }}
              className="group rounded-2xl border border-slate-200 bg-white p-6 hover:border-slate-300 sm:p-7"
              data-testid={`industry-overview-card-${ind.key}`}
            >
              <div className="flex items-start gap-4">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-blue-50 text-blue-700">
                  <ind.icon className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-lg font-extrabold tracking-tight text-slate-900">{t(`content.${ind.tk}.label`)}</h3>
                  <p className="mt-1 text-sm font-semibold text-slate-700">{t(`content.${ind.tk}.headline`)}</p>
                  <p className="mt-2 text-sm text-slate-600">{t(`content.${ind.tk}.summary`)}</p>
                  <span className="mt-3 inline-flex items-center text-sm font-semibold text-blue-700">
                    {t("cta.readPlaybook")} <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </section>

      {INDUSTRIES.map((ind, idx) => {
        const label = t(`content.${ind.tk}.label`);
        const bullets = t(`content.${ind.tk}.bullets`, { returnObjects: true }) || [];
        return (
          <section
            key={ind.key} id={ind.key}
            className={`scroll-mt-24 ${idx % 2 === 0 ? "bg-slate-50" : ""}`}
            data-testid={`industry-detail-${ind.key}`}
          >
            <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
              <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-800">
                    <ind.icon className="h-4 w-4" /> {label}
                  </div>
                  <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{t(`content.${ind.tk}.headline`)}</h2>
                  <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg">{t(`content.${ind.tk}.summary`)}</p>
                  <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {bullets.map((b) => (
                      <li key={b} className="flex items-center gap-2 text-sm text-slate-800">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" /> {b}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" className="mt-6 inline-block" data-testid={`industry-cta-${ind.key}`}>
                    <Button className="h-11 rounded-lg bg-blue-600 px-5 text-white hover:bg-blue-700">
                      {t("cta.buildEngine", { vertical: label.split(" ")[0] })}
                      <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
                  <h3 className="text-base font-bold text-slate-900">{t("common.industryEngages.title")}</h3>
                  <ul className="mt-3 space-y-3 text-sm text-slate-700">
                    {engagesItems.map((it) => (
                      <li key={it} className="flex items-start gap-3"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {it}</li>
                    ))}
                  </ul>
                  <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs leading-relaxed text-slate-600">{t("common.industryEngages.note")}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      <CTASection title={t("ctaSection.industriesTitle")} />
    </div>
  );
}
